import { yupResolver } from '@hookform/resolvers/yup';
import {
  Avatar,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCourseCore } from '../../../core/modules/course';
import * as yup from 'yup';
import { useGlobalCore } from '../../../core/modules/global';
import { CREATE_COURSE_DIALOG } from '../../../core/constants/dialog';
// import { useUserCore } from '../../../core/modules/user';
import { useNavigationCore } from '../../../core/router/navigation';
import { useTemplateCore } from '../../../core/modules/template';
import { PhotoCamera } from '@mui/icons-material';
import CourseAvatarPositionEditor from './CourseAvatarPositionEditor';

interface FormData {
  name: string;
  // professor: string;
  categories: string[];
  paykuCode?: string;
  paykuNCode?: string;
  paykuYCode?: string;
  hotmartCode?: string;
  template: string;
  classTemplate?: string;
  templateCancel?: string;
  whatsappUrl: string;
  description?: string;
}

const schema = yup.object().shape({
  name: yup.string().required('You must enter a name'),
  // professor: yup.string().required('You must enter a professor'),
  categories: yup.array(yup.string().required()).required('You must enter categories'),
  paykuCode: yup.string(),
  paykuNCode: yup.string(),
  paykuYCode: yup.string(),
  hotmartCode: yup.string(),
  template: yup.string().required('You must enter a template'),
  classTemplate: yup.string(),
  templateCancel: yup.string(),
  whatsappUrl: yup.string().url().required('You must enter a whatsapp url'),
  description: yup.string(),
});

const defaultValues: FormData = {
  name: '',
  // professor: '',
  categories: [],
  paykuCode: '',
  paykuNCode: '',
  paykuYCode: '',
  hotmartCode: '',
  template: '',
  classTemplate: '',
  templateCancel: '',
  whatsappUrl: '',
  description: '',
};

function CreateDialog() {
  const { dialogs, closeDialog } = useGlobalCore();
  // const { users } = useUserCore();
  const { goToCourse } = useNavigationCore();
  const { categories, postCourse } = useCourseCore();
  const { templates } = useTemplateCore();

  const [loading, setLoading]  = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null);
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState<string | null>(null);
  const [avatarPositionX, setAvatarPositionX] = useState(50);
  const [avatarPositionY, setAvatarPositionY] = useState(100);

  const { control, formState, handleSubmit, reset } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  const onSubmit = useCallback(
    ({
      name,
      categories,
      paykuCode = '',
      paykuNCode = '',
      paykuYCode = '',
      hotmartCode = '',
      template,
      classTemplate = '',
      templateCancel = '',
      whatsappUrl,
      description = '',
    }: FormData) => {
      setLoading(true);
      postCourse(
        {
          name,
          professor: '1',
          categories,
          paykuCode,
          paykuNCode,
          paykuYCode,
          hotmartCode,
          template,
          classTemplate,
          templateCancel,
          description,
          whatsappUrl,
          selectedAvatar,
          avatarPositionX: previewAvatarUrl ? avatarPositionX : undefined,
          avatarPositionY: previewAvatarUrl ? avatarPositionY : undefined,
        },
        (id: string) => {
          reset();
          if (previewAvatarUrl) {
            URL.revokeObjectURL(previewAvatarUrl);
          }
          setPreviewAvatarUrl(null);
          setAvatarPositionX(50);
          setAvatarPositionY(100);
          goToCourse(id);
          setLoading(false);
          closeDialog(CREATE_COURSE_DIALOG);
        },
        () => {
          setLoading(false);
        }
      );
    },
    [postCourse, selectedAvatar, reset, closeDialog, previewAvatarUrl, goToCourse, avatarPositionX, avatarPositionY]
  );

  useEffect(() => {
    return () => {
      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
      }
    };
  }, [previewAvatarUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedAvatar(file);
      const newPreviewUrl = URL.createObjectURL(file);

      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
      }
      setPreviewAvatarUrl(newPreviewUrl);
    }
  };

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, m: { xs: 1, sm: 3 } } }}
      onClose={() => closeDialog(CREATE_COURSE_DIALOG)}
      open={dialogs.includes(CREATE_COURSE_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography sx={{ fontWeight: 800, fontSize: 20 }}>
                Nuevo curso
              </Typography>
            </div>

            <div className="flex flex-col justify-center items-center mb-32">
              {previewAvatarUrl ? (
                <Avatar
                  alt="user photo"
                  src={previewAvatarUrl}
                  sx={{ width: 100, height: 100 }}
                />
              ) : (
                <Avatar sx={{ width: 100, height: 100 }}>
                  C
                </Avatar>
              )}
              <input
                accept="image/jpeg"
                style={{ display: 'none' }}
                id="avatar-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="avatar-upload">
                <Button color="primary" aria-label="upload picture" component="span" className="mt-2" disabled={loading}>
                  <PhotoCamera />
                  Elegir Avatar
                </Button>
              </label>
              {previewAvatarUrl && (
                <CourseAvatarPositionEditor
                  imageUrl={previewAvatarUrl}
                  positionX={avatarPositionX}
                  positionY={avatarPositionY}
                  onChange={(x, y) => {
                    setAvatarPositionX(x);
                    setAvatarPositionY(y);
                  }}
                />
              )}
            </div>

            <form
              name="projectForm"
              noValidate
              className="flex flex-col space-y-32"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nombre"
                    id="name"
                    error={!!errors.name}
                    helperText={errors?.name?.message}
                    variant="outlined"
                    disabled={loading}
                    required
                    fullWidth
                  />
                )}
              />

              {/* <FormControl fullWidth>
                <InputLabel id="course-professor-label">{t('label.professor')} *</InputLabel>
                <Controller
                  name="professor"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="course-professor-label"
                      label={t('label.professor')}
                      disabled={loading}
                    >
                      {users.map((user) => (
                        <MenuItem value={`${user.id}`} key={user.id}>{ user.name }</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl> */}

              <FormControl fullWidth>
                <InputLabel id="course-category-label">Categorías</InputLabel>
                <Controller
                  name="categories"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="course-category-label"
                      label="Categorías"
                      multiple
                      disabled={loading}
                      renderValue={(selected) => categories.filter(category =>
                        selected.indexOf(`${category.id}`) > -1
                      ).map(category => category.name).join(', ')}
                    >
                      {categories.map((category) => (
                        <MenuItem value={`${category.id}`} key={category.id}>
                          <Checkbox checked={field.value.indexOf(`${category.id}`) > -1} />
                          <ListItemText primary={category.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <TextField
                    {...field}
                    multiline
                    minRows={4}
                    maxRows={6}
                    label="Descripción"
                    id="description"
                    variant="outlined"
                    disabled={loading}
                    fullWidth
                  />
                )}
              />

              <Controller
                control={control}
                name="paykuCode"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Código Payku Suscripción"
                    id="paykuCode"
                    className="mr-4"
                    error={!!errors.paykuCode}
                    helperText={errors?.paykuCode?.message}
                    variant="outlined"
                    disabled={loading}
                    fullWidth
                  />
                )}
              />

              <Controller
                control={control}
                name="paykuNCode"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Código Payku Pago único"
                    id="paykuNCode"
                    className="mr-4"
                    error={!!errors.paykuNCode}
                    helperText={errors?.paykuNCode?.message}
                    variant="outlined"
                    disabled={loading}
                    fullWidth
                  />
                )}
              />

              <Controller
                control={control}
                name="paykuYCode"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Código Payku Pago anual"
                    id="paykuYCode"
                    className="mr-4"
                    error={!!errors.paykuYCode}
                    helperText={errors?.paykuYCode?.message}
                    variant="outlined"
                    disabled={loading}
                    fullWidth
                  />
                )}
              />

              <Controller
                control={control}
                name="hotmartCode"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Código Hotmart"
                    id="hotmartCode"
                    error={!!errors.hotmartCode}
                    helperText={errors?.hotmartCode?.message}
                    variant="outlined"
                    disabled={loading}
                    fullWidth
                  />
                )}
              />

              <FormControl fullWidth>
                <InputLabel id="course-template-label">Plantilla de Bienvenida *</InputLabel>
                <Controller
                  control={control}
                  name="template"
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="course-template-label"
                      label="Plantilla de Bienvenida"
                      disabled={loading}
                    >
                      {templates.filter((template) => template.type === 'course_welcome').map((template) => (
                        <MenuItem value={`${template.id}`} key={template.id}>{ template.name }</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <FormControl className="flex w-full mb-32 mr-8" variant="outlined">
                <InputLabel id="class-template-label">Plantilla de cancelación</InputLabel>
                <Controller
                  control={control}
                  name="templateCancel"
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="class-template-label"
                      label="Plantilla de clases"
                      required
                    >
                      <MenuItem value=''>Sin plantilla</MenuItem>
                      {templates.filter((template) => template.type === 'course_cancel').map((template) => (
                        <MenuItem value={`${template.id}`} key={template.id}>{ template.name }</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="class-template-label">Plantilla de clases</InputLabel>
                <Controller
                  control={control}
                  name="classTemplate"
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="class-template-label"
                      label="Plantilla de clases"
                      disabled={loading}
                    >
                      {templates.filter((template) => template.type === 'new_class').map((template) => (
                        <MenuItem value={`${template.id}`} key={template.id}>{ template.name }</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <Controller
                control={control}
                name="whatsappUrl"
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mb-32 mr-8"
                    label="Whatsapp"
                    id="whatsappUrl"
                    error={!!errors.whatsappUrl}
                    helperText={errors?.whatsappUrl?.message}
                    variant="outlined"
                    disabled={loading}
                    required
                    fullWidth
                  />
                )}
              />

              <div className="flex justify-end">
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ borderRadius: 1.75, boxShadow: 'none', textTransform: 'none', fontWeight: 700, py: 1.25 }}
                  disabled={!isEmpty(errors) || !isValid || loading}
                  type="submit"
                >
                  Guardar
                </Button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CreateDialog;
